import { Box, Button } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useMandatorContext } from "@bn/theming";
import { useAppLayout } from "@providers/AppLayoutProvider";
import { useMyself } from "@api/v1/hooks/useMyself";
import { Logo } from "@components/Logo";
import { Profile } from "../Profile";

const Container = styled(Box)<{ collapsed: number }>`
  align-items: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
  gap: ${({ theme, collapsed }) => theme.spacing(collapsed ? 1 : 0.5)};
`;

const PlusTypography = styled(Box)`
  color: ${({ theme }) => theme.palette.common.black};
  background-color: ${({ theme }) => theme.palette.primary.light};
  font-weight: 600;
  font-size: 10px;
  padding: ${({ theme }) => ` ${theme.spacing(1)} ${theme.spacing(2)}`};
  border-radius: 6px;
  display: flex;
  align-items: center;
  height: 24px;
`;

const ProTypography = styled(PlusTypography)`
  background-color: ${({ theme }) =>
    theme.palette.button.upgradeButton?.default};
  color: ${({ theme }) => theme.palette.button.upgradeButton?.textOnDefault};
`;

export interface AppSidebarHeaderProps {
  onTestPremiumClick?: () => void;
  onLogoClick?: () => void;
  collapsed?: boolean;
}

export const AppSidebarHeader = ({
  onTestPremiumClick,
  onLogoClick,
}: AppSidebarHeaderProps) => {
  const { t } = useTranslation();
  const { data: myself } = useMyself();
  const { isAriva } = useMandatorContext();

  const { sidebarCollapsed: collapsed } = useAppLayout();

  // Ariva hides the top section (logo + Test Premium); Profile becomes the top.
  if (isAriva) return;

  return (
    <Container
      display="flex"
      flexDirection="column"
      collapsed={collapsed ? 1 : 0}
      data-testid="header-container"
    >
      {collapsed && <Logo variant="superCompact" onClick={onLogoClick} />}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        {!collapsed && <Logo variant="sidebar" onClick={onLogoClick} />}
        {myself?.isUserPlus && !collapsed && (
          <PlusTypography>
            <span>{t("common.plus")}</span>
          </PlusTypography>
        )}
        {myself?.isUserPro && !collapsed && (
          <Box px={3}>
            <ProTypography>
              <span>{t("common.pro")}</span>
            </ProTypography>{" "}
          </Box>
        )}
        {myself?.isUserFree && !collapsed && (
          <motion.div
            animate={{
              opacity: collapsed ? 0 : 1,
              transition: { delay: 0.15 },
            }}
          >
            {!collapsed && (
              <Button
                onClick={onTestPremiumClick}
                variant="primary"
                color="upgrade"
                size="small"
              >
                {t("sidebar.header.testPremium")}
              </Button>
            )}
          </motion.div>
        )}
      </Box>
      <Box width="100%">
        <Profile />
      </Box>
    </Container>
  );
};
